import { useState } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  IconButton,
  Heading,
  Avatar,
  Badge,
  Separator,
} from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { FiTrash2, FiEdit2, FiCheck, FiUserX } from 'react-icons/fi'
import type { DrawnTurf } from '../../hooks/useTerraDraw'
import { HOUSEHOLD_STATUS_KEY } from './householdPaint'
import { AuthedAvatarImage } from '../shared'

// Readonly turf type for overview display
export interface ReadonlyTurf {
  id: string
  index: number
  label: string
  color: string
  contactCount: number
  householdCount: number
}

export interface TurfLegendMethods {
  deleteTurf: (id: string) => void
  updateTurfLabel: (id: string, label: string) => void
  flyToTurf: (id: string) => void
}

interface TurfLegendPanelProps {
  turfs: DrawnTurf[] | ReadonlyTurf[]
  readonly?: boolean
  /** Methods for interacting with turfs — pass from useTerraDraw or parent component */
  methods?: TurfLegendMethods
  /** Assignment map: turfIndex (string) → canvasserId | null */
  assignments?: Map<string, string | null>
  /** Canvasser lookup: canvasserId → display info */
  canvasserLookup?: Map<string, { fullName: string; portraitUrl: string | null }>
  /** Called when clicking the assignment avatar/icon for a turf */
  onAssignmentClick?: (turfKey: string) => void
  /** Coverage data: turfIndex (string) → { visited, total } */
  coverageData?: Map<string, { visited: number; total: number }>
  /** Render only the inner content (no floating wrapper) for embedding in a bottom sheet */
  bare?: boolean
  /** Show the household dot colour key (visit status) — set where the map paints status-coloured household dots */
  showHouseholdStatusKey?: boolean
}

function isDrawnTurf(turf: DrawnTurf | ReadonlyTurf): turf is DrawnTurf {
  return 'contactIds' in turf
}

export function TurfLegendPanel({
  turfs,
  readonly = false,
  methods,
  assignments,
  canvasserLookup,
  onAssignmentClick,
  coverageData,
  bare = false,
  showHouseholdStatusKey = false,
}: TurfLegendPanelProps) {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editLabel, setEditLabel] = useState('')

  // Fallback to window API for backwards compat (TurfCutterPage transition)
  const resolvedMethods: TurfLegendMethods | undefined = methods ?? (
    typeof window !== 'undefined'
      ? (window as any).__multiTurfMapMethods as TurfLegendMethods | undefined
      : undefined
  )

  const handleStartEdit = (turf: DrawnTurf) => {
    setEditingId(turf.id)
    setEditLabel(turf.label)
  }

  const handleSaveEdit = (turfId: string) => {
    if (resolvedMethods && editLabel.trim()) {
      resolvedMethods.updateTurfLabel(turfId, editLabel.trim())
    }
    setEditingId(null)
    setEditLabel('')
  }

  const handleDelete = (turfId: string) => {
    if (resolvedMethods) {
      resolvedMethods.deleteTurf(turfId)
    }
  }

  const handleTurfClick = (turfId: string) => {
    if (resolvedMethods) {
      resolvedMethods.flyToTurf(turfId)
    }
  }

  // Calculate totals
  const totalContacts = turfs.reduce((sum, t) => {
    return sum + (isDrawnTurf(t) ? t.contactIds.length : t.contactCount)
  }, 0)
  const totalHouseholds = turfs.reduce((sum, t) => sum + t.householdCount, 0)

  const statusKey = showHouseholdStatusKey ? (
    <Box data-testid="household-status-key">
      <Text fontSize="2xs" fontWeight="medium" textTransform="uppercase" color="ink.2" mb={1}>
        Households
      </Text>
      <HStack gap={3} wrap="wrap">
        {HOUSEHOLD_STATUS_KEY.map(item => (
          <HStack key={item.key} gap={1}>
            <Box w="8px" h="8px" borderRadius="full" bg={item.color} flexShrink={0} />
            <Text fontSize="xs" color="ink.2">{item.label}</Text>
          </HStack>
        ))}
      </HStack>
    </Box>
  ) : null

  const content = (
    <VStack gap={3} align="stretch">
        <Heading size="sm">Turfs</Heading>
        <Separator />

        {turfs.length === 0 && (
          <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
            Draw a polygon to create a turf
          </Text>
        )}

        {turfs.map(turf => {
          const turfKey = String(turf.index)
          const canvasserId = assignments?.get(turfKey) ?? null
          const canvasser = canvasserId && canvasserLookup ? canvasserLookup.get(canvasserId) : null
          const coverage = coverageData?.get(turfKey)
          const pct = coverage && coverage.total > 0
            ? Math.round((coverage.visited / coverage.total) * 100)
            : null

          return (
            <Box
              key={turf.id}
              p={2}
              borderRadius="md"
              _hover={{ bg: 'gray.50', cursor: 'pointer' }}
              onClick={() => handleTurfClick(turf.id)}
            >
              <HStack gap={2} align="flex-start">
                {/* Color swatch */}
                <Box
                  w="16px"
                  h="16px"
                  borderRadius="sm"
                  bg={turf.color}
                  flexShrink={0}
                  mt={1}
                />

                {/* Label (editable or readonly) */}
                <VStack gap={0} align="stretch" flex={1} minW={0}>
                  {!readonly && editingId === turf.id ? (
                    <HStack gap={1}>
                      <Input
                        size="xs"
                        value={editLabel}
                        onChange={e => setEditLabel(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleSaveEdit(turf.id)
                          if (e.key === 'Escape') setEditingId(null)
                        }}
                        onClick={e => e.stopPropagation()}
                        autoFocus
                      />
                      <IconButton
                        aria-label="Save"
                        size="xs"
                        colorPalette="green"
                        onClick={e => {
                          e.stopPropagation()
                          handleSaveEdit(turf.id)
                        }}><FiCheck /></IconButton>
                    </HStack>
                  ) : (
                    <HStack justify="space-between">
                      <Text fontSize="sm" fontWeight="medium" lineClamp={1}>
                        {turf.label}
                      </Text>
                      {!readonly && isDrawnTurf(turf) && (
                        <IconButton
                          aria-label="Edit label"
                          size="xs"
                          variant="ghost"
                          onClick={e => {
                            e.stopPropagation()
                            handleStartEdit(turf as DrawnTurf)
                          }}><FiEdit2 /></IconButton>
                      )}
                    </HStack>
                  )}

                  {/* Counts + coverage */}
                  <HStack fontSize="xs" color="gray.600" gap={2}>
                    <Text>
                      {isDrawnTurf(turf) ? turf.contactIds.length : turf.contactCount} contacts
                    </Text>
                    <Text>{turf.householdCount} hh</Text>
                    {pct != null && (
                      <Badge
                        size="sm"
                        colorPalette={pct >= 80 ? 'green' : pct >= 40 ? 'yellow' : 'red'}
                        fontSize="2xs"
                      >
                        {pct}%
                      </Badge>
                    )}
                  </HStack>
                </VStack>

                {/* Assignment avatar (optional) */}
                {assignments && (
                  <Box
                    flexShrink={0}
                    onClick={e => {
                      e.stopPropagation()
                      onAssignmentClick?.(turfKey)
                    }}
                    cursor="pointer"
                  >
                    {canvasser ? (
                      <Tooltip contentProps={{ fontSize: "xs" }} content={`${canvasser.fullName} — click to reassign`} showArrow>
                        <Avatar.Root size="xs"><Avatar.Fallback name={canvasser.fullName} /><AuthedAvatarImage src={canvasser.portraitUrl} /></Avatar.Root>
                      </Tooltip>
                    ) : (
                      <Tooltip contentProps={{ fontSize: "xs" }} content="Click to assign canvasser" showArrow>
                        <Box>
                          <FiUserX color="gray" size={16} />
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                )}

                {/* Delete button (only in editable mode) */}
                {!readonly && (
                  <IconButton
                    aria-label="Delete turf"
                    size="xs"
                    colorPalette="red"
                    variant="ghost"
                    flexShrink={0}
                    onClick={e => {
                      e.stopPropagation()
                      handleDelete(turf.id)
                    }}><FiTrash2 /></IconButton>
                )}
              </HStack>
            </Box>
          );
        })}

        {turfs.length > 0 && (
          <>
            <Separator />
            <Box p={2} bg="gray.50" borderRadius="md">
              <HStack justify="space-between">
                <Text fontSize="sm" fontWeight="bold">
                  Total
                </Text>
                <VStack gap={0} align="flex-end">
                  <Text fontSize="xs">{totalContacts} contacts</Text>
                  <Text fontSize="xs">{totalHouseholds} households</Text>
                </VStack>
              </HStack>
            </Box>
          </>
        )}

    </VStack>
  )

  if (bare) return (
    <VStack gap={3} align="stretch">
      {content}
      {statusKey}
    </VStack>
  )

  // Only the turf list scrolls; the panel itself clips (overflow hidden). Capture review
  // 2026-10-24: with the whole panel as the overflow:auto scroller, the key sat below the fold
  // and the unclipped scroll layer painted an empty white block over the map beneath the panel.
  return (
    <Box
      data-testid="turf-legend-floating"
      position="absolute"
      top={4}
      left={4}
      bg="surface"
      p={3}
      borderRadius="md"
      shadow="lg"
      w="260px"
      maxH="60%"
      display="flex"
      flexDirection="column"
      overflow="hidden"
      zIndex={10}
    >
      <Box data-testid="turf-legend-scroll" flex="1" minH={0} overflowY="auto">
        {content}
      </Box>
      {statusKey && (
        <Box flexShrink={0} pt={2} mt={2} borderTopWidth="1px" borderColor="border">
          {statusKey}
        </Box>
      )}
    </Box>
  )
}
